/**
 * 任务管理 Store (Pinia)
 *
 * 职责：
 * - 任务状态管理（单次/批量）
 * - SSE 实时订阅
 * - 任务过滤和排序
 * - 任务操作（创建/取消/重试/删除）
 *
 * @version 2.0.0 - 重构版本
 * @author Frontend Team
 * @since 2025-10-22
 */

import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { taskAPI } from '@/api/task'
import { RetryFailedTaskItems } from '@/api/tts'
import { adaptTasks } from '@/utils/data-adapter'
import { SSEManager } from '@/utils/sse-manager'
import { generateBrowserFingerprint } from '@/utils/app-runtime'
import type {
  Task
} from '@/types/task.types'
import {
  TASK_STATUS
} from '@/types/task.types'
import type {
  CreateSingleTaskDTO,
  CreateBatchTaskDTO,
  TaskFilterDTO,
  TaskSortDTO
} from '@/types/task.dto'
import type { TaskEvent } from '@/types/task.events'

export const useTaskStore = defineStore('task', () => {
  // ============= 状态定义 =============

  // 任务列表
  const tasks = ref<Task[]>([])
  const currentTask = ref<Task | null>(null)
  const isLoading = ref(false)
  const isCreating = ref(false)
  const error = ref<string | null>(null)

  // 过滤和排序
  const filter = ref<TaskFilterDTO>({})
  const sort = ref<TaskSortDTO>({ field: 'createdAt', order: 'desc' })

  // 分页
  const currentPage = ref(1)
  const pageSize = ref(20)
  const total = ref(0)

  // 选择状态
  const selectedTaskIds = ref<string[]>([])

  // 任务历史
  const taskHistory = ref<Task[]>([])

  // SSE 订阅管理
  let globalSSE: SSEManager<TaskEvent> | null = null
  const isSubscribed = ref(false)

  // SSE 调试标志（开发环境启用）
  const sseDebugEnabled = ref(import.meta.env.DEV)

  // SSE 连接状态
  const sseConnectionStatus = ref<'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'error'>('disconnected')

  // 是否曾经尝试过连接（用于区分初始状态和真正的断开）
  const hasEverConnected = ref(false)

  // 初始化门闩：只有完成首次任务加载后才允许自动管理 SSE
  const isInitialized = ref(false)

  // 延迟取消订阅的定时器（避免抖动）
  let unsubscribeTimer: any = null

  // ============= 计算属性 =============

  /**
   * 过滤后的任务列表
   */
  const filteredTasks = computed(() => {
    let result = tasks.value

    // 应用过滤条件
    if (filter.value.status && filter.value.status !== 'all') {
      const statuses = Array.isArray(filter.value.status)
        ? filter.value.status
        : [filter.value.status]

      // 处理特殊状态值
      if (statuses.includes('!completed')) {
        // 排除已完成的任务
        result = result.filter(task => task.status !== 'completed')
      } else {
        // 正常状态过滤
        result = result.filter(task => statuses.includes(task.status))
      }
    }

    if (filter.value.type) {
      const types = Array.isArray(filter.value.type)
        ? filter.value.type
        : [filter.value.type]
      result = result.filter(task => types.includes(task.type))
    }

    if (filter.value.keyword) {
      const keyword = filter.value.keyword.toLowerCase()
      result = result.filter(task =>
        task.title.toLowerCase().includes(keyword) ||
        (task.description && task.description.toLowerCase().includes(keyword))
      )
    }

    if (filter.value.dateRange) {
      const [startDate, endDate] = filter.value.dateRange
      result = result.filter(task => {
        const taskDate = new Date(task.createdAt)
        return taskDate >= new Date(startDate) && taskDate <= new Date(endDate)
      })
    }

    if (filter.value.voices && filter.value.voices.length > 0) {
      result = result.filter(task => {
        if (task.type === 'single') {
          return filter.value.voices!.includes(task.voice)
        }
        return true
      })
    }

    if (filter.value.isFavorite !== undefined) {
      result = result.filter(task => {
        return task.artifacts?.some(a => a.favorite === filter.value.isFavorite)
      })
    }

    // 应用排序
    result.sort((a, b) => {
      const field = sort.value.field as keyof Task
      const order = sort.value.order

      let aValue = a[field]
      let bValue = b[field]

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }

      if (aValue === undefined || bValue === undefined) {
        return 0
      }

      if (order === 'asc') {
        return aValue > bValue ? 1 : -1
      } else {
        return aValue < bValue ? 1 : -1
      }
    })

    return result
  })

  /**
   * 任务统计
   */
  const taskStats = computed(() => {
    const stats = {
      total: tasks.value.length,
      created: 0,
      queued: 0,
      processing: 0,
      post_processing: 0,
      completed: 0,
      failed: 0,
      cancelled: 0
    }

    tasks.value.forEach(task => {
      if (task.status && stats.hasOwnProperty(task.status)) {
        (stats as any)[task.status]++
      }
    })

    return stats
  })

  /**
   * 运行中的任务
   */
  const runningTasks = computed(() => {
    return tasks.value.filter(task =>
      task.status === TASK_STATUS.QUEUED ||
      task.status === TASK_STATUS.PROCESSING ||
      task.status === TASK_STATUS.POST_PROCESSING
    )
  })

  /**
   * 单次任务列表（暂未使用）
   */
  // const singleTasks = computed(() => {
  //   return tasks.value.filter(task => task.type === 'single') as SingleTask[]
  // })

  /**
   * 批量任务列表（暂未使用）
   */
  // const batchTasks = computed(() => {
  //   return tasks.value.filter(task => task.type === 'batch') as BatchTask[]
  // })

  // ============= Actions =============

  /**
   * 处理任务事件
   */
  const handleTaskEvent = (event: TaskEvent) => {
    const { taskId, type, data } = event
    const taskIndex = tasks.value.findIndex(t => t.taskId === taskId)

    if (taskIndex === -1) {
      // 任务不在列表中，可能需要重新获取
      console.log(`收到未知任务 ${taskId} 的事件，类型: ${type}`)
      return
    }

    const task = tasks.value[taskIndex]

    switch (type) {
      case 'progress':
        // 更新进度
        if ('progress' in data) {
          task.progress = data.progress
        }
        if ('status' in data) {
          task.status = data.status as any
        }
        break

      case 'completed':
        // 任务完成
        task.status = 'completed'
        task.progress = 100
        if ('completedAt' in data) {
          task.metadata = { ...task.metadata, completedAt: data.completedAt }
        }
        if ('artifacts' in data && data.artifacts) {
          task.artifacts = data.artifacts as any
        }
        // 移除重复的成功提示，由 useTTSEvents 统一处理
        break

      case 'failed':
      case 'processing_failed':
      case 'post_failed':
        // 任务失败（包括处理阶段失败和后处理阶段失败）
        task.status = type as any  // 保持原始状态：failed/processing_failed/post_failed
        if ('error' in data) {
          task.error = data.error as string
        }
        if ('failurePhase' in data) {
          task.failurePhase = data.failurePhase as any
        }
        // 注意：不在这里显示错误提示，由上层业务逻辑（useTTSEvents）统一处理
        break

      case 'cancelled':
        // 任务取消
        task.status = 'cancelled'
        ElMessage.info(`任务 "${task.title}" 已取消`)
        break

      case 'item_progress':
      case 'item_completed':
      case 'item_failed':
        // 批量任务的子项事件
        if (task.type === 'batch' && 'itemId' in data) {
          const itemIndex = task.items?.findIndex(item => item.id === (data as any).itemId)
          if (itemIndex !== undefined && itemIndex !== -1 && task.items) {
            const item = task.items[itemIndex]
            if (type === 'item_progress' && 'progress' in data) {
              item.progress = data.progress
            } else if (type === 'item_completed') {
              item.status = 'completed'
              item.progress = 100
              task.completedItems = (task.completedItems || 0) + 1
            } else if (type === 'item_failed') {
              item.status = 'failed'
              if ('error' in data) {
                item.errorMsg = data.error as string
              }
              task.failedItems = (task.failedItems || 0) + 1
            }
          }
        }
        break
    }

    // 触发响应式更新
    tasks.value[taskIndex] = { ...task }
  }

  /**
   * 检查是否有进行中的任务
   */
  const hasActiveTasks = computed(() => {
    if (!tasks.value.length) return false
    return tasks.value.some(task =>
      ['created', 'queued', 'processing', 'post_processing'].includes(task.status)
    )
  })

  /**
   * 智能管理SSE订阅（根据是否有进行中任务自动订阅/取消订阅）
   *
   * 注意：现在改为只管理取消订阅，订阅由调用方（useTTS）负责预连接
   */
  const manageSSESubscription = () => {
    // 门闩保护：未初始化时不自动订阅
    if (!isInitialized.value) {
      return
    }

    // 清除之前的延迟取消订阅定时器
    if (unsubscribeTimer) {
      clearTimeout(unsubscribeTimer)
      unsubscribeTimer = null
    }

    // ✅ 移除自动订阅逻辑，只保留自动取消订阅
    if (!hasActiveTasks.value && isSubscribed.value) {
      // 没有进行中任务且已订阅：延迟5秒后取消订阅（避免抖动）
      unsubscribeTimer = setTimeout(() => {
        // 再次检查是否还是没有活跃任务
        if (!hasActiveTasks.value && isSubscribed.value) {
          unsubscribeGlobal()
          // 当没有进行中任务时，重置连接状态，避免显示"连接已断开"
          sseConnectionStatus.value = 'disconnected'
          hasEverConnected.value = false
        }
      }, 5000)
    }
  }

  /**
   * 订阅全局任务流（所有任务）
   * @returns Promise，在连接建立后 resolve
   */
  const subscribeGlobal = (): Promise<void> => {
    // 如果已经订阅，直接返回
    if (globalSSE || isSubscribed.value) {
      return Promise.resolve()
    }

    return new Promise((resolve) => {
      // EventSource不支持自定义头，通过URL参数传递指纹
      const fingerprint = generateBrowserFingerprint()
      const url = `/api/v1/tasks/stream?fingerprint=${encodeURIComponent(fingerprint)}`

      // 设置超时（10秒）
      const timeout = setTimeout(() => {
        resolve() // 即使超时也 resolve，不阻塞任务创建
      }, 10000)

      globalSSE = new SSEManager<TaskEvent>({
        url,
        withCredentials: true,
        debug: sseDebugEnabled.value,
        onOpen: () => {
          clearTimeout(timeout)
          sseConnectionStatus.value = 'connected'
          hasEverConnected.value = true
          isSubscribed.value = true
          resolve()
        },
        onClose: () => {
          sseConnectionStatus.value = 'disconnected'
          isSubscribed.value = false
        },
        onError: (err) => {
          clearTimeout(timeout)
          console.error('[TaskStore] SSE 连接错误:', err)
          sseConnectionStatus.value = 'error'
          isSubscribed.value = false
          // 不 reject，避免阻塞任务创建
          resolve()
        }
      })

      // 监听所有事件类型
      globalSSE.on('progress', handleTaskEvent)
      globalSSE.on('completed', handleTaskEvent)
      globalSSE.on('failed', handleTaskEvent)
      globalSSE.on('processing_failed', handleTaskEvent)  // ✅ 处理阶段失败
      globalSSE.on('post_failed', handleTaskEvent)        // ✅ 后处理阶段失败
      globalSSE.on('cancelled', handleTaskEvent)
      globalSSE.on('item_progress', handleTaskEvent)
      globalSSE.on('item_completed', handleTaskEvent)
      globalSSE.on('item_failed', handleTaskEvent)
      globalSSE.on('ping', () => {
        // 心跳事件，保持连接活跃（收到 ping 事件会自动更新 lastMessageAt）
      })
    })
  }

  /**
   * 取消全局订阅
   */
  const unsubscribeGlobal = () => {
    if (globalSSE) {
      globalSSE.close()
      globalSSE = null
      isSubscribed.value = false
    }
  }





  /**
   * 创建单次任务
   */
  const createSingleTask = async (request: CreateSingleTaskDTO): Promise<Task> => {
    try {
      isCreating.value = true
      error.value = null

      // 标记为已初始化（创建任务也算初始化）
      if (!isInitialized.value) {
        isInitialized.value = true
      }

      // ✅ 移除自动订阅逻辑，由调用方（useTTS）负责预连接

      const task = await taskAPI.createSingleTask(request)

      // 添加到任务列表
      tasks.value.unshift(task)
      currentTask.value = task

      ElMessage.success('任务创建成功')
      return task
    } catch (err: any) {
      error.value = err.message || '创建任务失败'
      ElMessage.error(error.value || '创建任务失败')
      throw err
    } finally {
      isCreating.value = false
    }
  }

  /**
   * 创建批量任务
   */
  const createBatchTask = async (request: CreateBatchTaskDTO): Promise<Task> => {
    try {
      isCreating.value = true
      error.value = null

      // 标记为已初始化（创建任务也算初始化）
      if (!isInitialized.value) {
        isInitialized.value = true
      }

      // ✅ 移除自动订阅逻辑，由调用方负责预连接

      const task = await taskAPI.createBatchTask(request)

      // 添加到任务列表
      tasks.value.unshift(task)
      currentTask.value = task

      ElMessage.success('批量任务创建成功')
      return task
    } catch (err: any) {
      error.value = err.message || '创建批量任务失败'
      ElMessage.error(error.value || '创建批量任务失败')
      throw err
    } finally {
      isCreating.value = false
    }
  }

  /**
   * 创建任务（统一入口）
   */
  const createTask = async (request: CreateSingleTaskDTO | CreateBatchTaskDTO): Promise<Task> => {
    if (request.type === 'batch') {
      return createBatchTask(request as CreateBatchTaskDTO)
    } else {
      return createSingleTask(request as CreateSingleTaskDTO)
    }
  }
  
  /**
   * 获取任务列表
   */
  const fetchTasks = async (params?: {
    page?: number;
    pageSize?: number;
    filter?: TaskFilterDTO;
    sort?: TaskSortDTO;
  }) => {
    try {
      isLoading.value = true;
      error.value = null;

      const queryParams = {
        page: params?.page || currentPage.value,
        pageSize: params?.pageSize || pageSize.value,
        filter: params?.filter || filter.value,
        sort: params?.sort || sort.value
      };

      const responseData = await taskAPI.getTasks(queryParams);

      // taskAPI 已使用新SDK，直接返回解包后的业务数据
      tasks.value = adaptTasks(responseData.tasks);
      total.value = responseData.total;
      currentPage.value = responseData.page;
      pageSize.value = responseData.pageSize;

      // 标记为已初始化（首次加载完成后才允许自动管理 SSE）
      if (!isInitialized.value) {
        isInitialized.value = true;
        // 初始化完成后，检查是否有进行中的任务需要订阅
        manageSSESubscription();
      }
    } catch (err: any) {
      error.value = err.message || '获取任务列表失败';
      ElMessage.error(error.value || '获取任务列表失败');
    } finally {
      isLoading.value = false;
    }
  };
  


  /**
   * 更新任务状态（本地更新，暂无API支持）
   */
  const updateTask = async (taskId: string, updates: Partial<Task>) => {
    try {
      // TODO: 等待后端提供 updateTask API
      // const responseData = await taskAPI.updateTask(taskId, updates);

      // 暂时只做本地更新
      const index = tasks.value.findIndex(t => t.taskId === taskId);

      if (index > -1) {
        tasks.value[index] = { ...tasks.value[index], ...updates } as Task;
      }

      if (currentTask.value && currentTask.value.taskId === taskId) {
        currentTask.value = { ...currentTask.value, ...updates } as Task;
      }

      return tasks.value[index];
    } catch (err: any) {
      ElMessage.error(err.message || '更新任务失败');
      throw err;
    }
  };
  
  /**
   * 删除任务
   */
  const deleteTask = async (taskId: string) => {
    try {
      await taskAPI.deleteTask(taskId);

      // 删除成功，从列表中移除
      const index = tasks.value.findIndex(t => t.taskId === taskId);
      if (index > -1) {
        tasks.value.splice(index, 1);
      }

      if (currentTask.value && currentTask.value.taskId === taskId) {
        currentTask.value = null;
      }

      ElMessage.success('任务删除成功');
    } catch (err: any) {
      ElMessage.error(err.message || '删除任务失败');
      throw err;
    }
  };
  
  /**
   * 刷新任务状态
   */
  const refreshTaskStatus = async (taskId: string) => {
    try {
      const task = tasks.value.find(t => t.taskId === taskId);
      if (!task) {
        throw new Error('任务不存在');
      }

      // 这里应该调用后端API获取最新状态
      // 暂时使用现有的 fetchTasks 方法
      await fetchTasks();

      ElMessage.success('任务状态已刷新');
    } catch (err: any) {
      ElMessage.error(err.message || '刷新任务状态失败');
      throw err;
    }
  };

  /**
   * 取消任务
   */
  const cancelTask = async (taskId: string) => {
    try {
      await updateTask(taskId, {
        status: 'cancelled',
        updatedAt: new Date().toISOString()
      });

      ElMessage.success('任务已取消');
    } catch (err: any) {
      ElMessage.error(err.message || '取消任务失败');
      throw err;
    }
  };
  
  /**
   * 重试任务
   */
  const retryTask = async (taskId: string) => {
    try {
      await updateTask(taskId, {
        status: 'created',
        progress: 0,
        error: undefined,
        updatedAt: new Date().toISOString()
      });

      ElMessage.success('任务已重新开始');
    } catch (err: any) {
      ElMessage.error(err.message || '重试任务失败');
      throw err;
    }
  };

  /**
   * 设置过滤条件
   */
  const setFilter = (newFilter: TaskFilterDTO) => {
    filter.value = { ...filter.value, ...newFilter };
  };

  /**
   * 设置排序条件
   */
  const setSort = (newSort: TaskSortDTO) => {
    sort.value = newSort;
  };
  
  /**
   * 清除错误状态
   */
  const clearError = () => {
    error.value = null;
  };
  
  /**
   * 重置状态
   */
  const reset = () => {
    tasks.value = [];
    currentTask.value = null;
    taskHistory.value = [];
    error.value = null;
    filter.value = {};
    sort.value = { field: 'createdAt', order: 'desc' };
    currentPage.value = 1;
    total.value = 0;
    selectedTaskIds.value = [];
  };

  // ============= 缺失的方法 =============

  /**
   * 加载任务列表 (loadTasks 的别名)
   */
  const loadTasks = async (forceRefresh = false) => {
    return await fetchTasks(forceRefresh ? { page: 1 } : undefined);
  };

  /**
   * 加载用户权益限制
   */
  const loadUserLimits = async () => {
    // 这里应该调用相应的API来获取用户权益
    // 暂时返回空实现，避免报错
    console.log('loadUserLimits called - 需要实现具体逻辑');
  };

  /**
   * 停止状态轮询
   */
  const stopStatusPolling = () => {
    // 这里应该停止任务状态轮询
    // 暂时返回空实现，避免报错
    console.log('stopStatusPolling called - 需要实现具体逻辑');
  };

  /**
   * 清空所有任务
   */
  const clearAllTasks = async () => {
    try {
      // 这里应该调用API清空所有任务
      tasks.value = [];
      total.value = 0;
      console.log('clearAllTasks called - 任务已清空');
    } catch (err: any) {
      ElMessage.error(err.message || '清空任务失败');
      throw err;
    }
  };

  /**
   * 删除多个任务
   */
  const deleteTasks = async (taskIds: string[]) => {
    try {
      // 这里应该调用API删除多个任务
      for (const taskId of taskIds) {
        await deleteTask(taskId);
      }
      console.log('deleteTasks called - 任务已删除');
    } catch (err: any) {
      ElMessage.error(err.message || '删除任务失败');
      throw err;
    }
  };

  /**
   * 批量取消任务
   */
  const batchCancelTasks = async (taskIds: string[]) => {
    try {
      const results = await Promise.allSettled(
        taskIds.map(taskId => cancelTask(taskId))
      );

      const successCount = results.filter(r => r.status === 'fulfilled').length;
      const failCount = results.filter(r => r.status === 'rejected').length;

      if (successCount > 0) {
        ElMessage.success(`成功取消 ${successCount} 个任务`);
      }
      if (failCount > 0) {
        ElMessage.warning(`${failCount} 个任务取消失败`);
      }

      await fetchTasks();
    } catch (err: any) {
      ElMessage.error(err.message || '批量取消失败');
      throw err;
    }
  };

  /**
   * 批量重试任务
   */
  const batchRetryTasks = async (taskIds: string[]) => {
    try {
      const results = await Promise.allSettled(
        taskIds.map(taskId => retryTask(taskId))
      );

      const successCount = results.filter(r => r.status === 'fulfilled').length;
      const failCount = results.filter(r => r.status === 'rejected').length;

      if (successCount > 0) {
        ElMessage.success(`成功重试 ${successCount} 个任务`);
      }
      if (failCount > 0) {
        ElMessage.warning(`${failCount} 个任务重试失败`);
      }

      await fetchTasks();
    } catch (err: any) {
      ElMessage.error(err.message || '批量重试失败');
      throw err;
    }
  };

  /**
   * 重试失败的任务项
   */
  const retryFailedTaskItems = async (taskId: string): Promise<boolean> => {
    try {
      await RetryFailedTaskItems(taskId);
      ElMessage.success('失败的任务项已重新开始');
      await fetchTasks();
      return true;
    } catch (err: any) {
      ElMessage.error(err.message || '重试失败的任务项失败');
      return false;
    }
  };

  /**
   * 下载单个任务结果
   */
  const downloadTaskResults = async (taskId: string, format: 'zip' | 'json' = 'zip') => {
    try {
      // 使用 tts API 下载任务结果
      const { downloadTaskResults } = await import('@/api/tts');
      const result = await downloadTaskResults(taskId, format);

      if (format === 'zip' && result instanceof Blob) {
        // 创建下载链接
        const url = URL.createObjectURL(result);
        const link = document.createElement('a');
        link.href = url;
        link.download = `task-${taskId}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        ElMessage.success('任务结果下载已开始');
      } else {
        ElMessage.success('任务结果下载已开始');
      }
    } catch (err: any) {
      console.error('下载任务结果失败:', err);
      ElMessage.error(err.message || '下载任务结果失败');
      throw err;
    }
  };

  /**
   * 批量下载任务结果
   */
  const batchDownloadTasks = async (taskIds: string[], _format: 'zip' | 'individual' = 'zip') => {
    try {
      for (const taskId of taskIds) {
        await downloadTaskResults(taskId, 'zip');
        // 添加延迟避免浏览器阻止多个下载
        if (taskIds.length > 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      ElMessage.success(`开始下载 ${taskIds.length} 个任务的结果`);
    } catch (err: any) {
      ElMessage.error(err.message || '批量下载失败');
      throw err;
    }
  };

  // ============= 响应式监听 =============

  // 监听任务变化，自动管理SSE订阅
  watch(hasActiveTasks, () => {
    manageSSESubscription()
  })

  return {
    // 状态
    tasks,
    currentTask,
    taskHistory,
    isLoading,
    isCreating,
    error,
    filter,
    sort,
    currentPage,
    pageSize,
    total,
    selectedTaskIds,
    isSubscribed,
    sseConnectionStatus,
    hasEverConnected, // 导出新状态

    // 计算属性
    filteredTasks,
    taskStats,
    runningTasks,
    hasActiveTasks,

    // 方法
    createTask,
    fetchTasks,
    loadTasks, // 新增
    updateTask,
    deleteTask,
    deleteTasks, // 新增
    batchCancelTasks, // 新增
    batchRetryTasks, // 新增
    downloadTaskResults, // 新增
    batchDownloadTasks, // 新增
    refreshTaskStatus, // 新增
    cancelTask,
    retryTask,
    retryFailedTaskItems, // 新增
    setFilter,
    setSort,
    clearError,
    clearAllTasks, // 新增
    loadUserLimits, // 新增
    stopStatusPolling, // 新增
    reset,

    // SSE 订阅方法
    subscribeGlobal,
    unsubscribeGlobal,
    manageSSESubscription,

    // SSE 调试方法
    getSSEMetrics: () => {
      const metrics = {
        global: globalSSE?.getMetrics() || null
      }
      return metrics
    },
    toggleSSEDebug: (enabled: boolean) => {
      sseDebugEnabled.value = enabled
    }
  };
});
